import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, CreditCard, ExternalLink, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

function AdminPaymentIntents() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [eventType, setEventType] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ date, event_type: eventType });
      const res = await API.get(`/admin/payment-intents?${params.toString()}`);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setSummary(Array.isArray(res.data?.summary) ? res.data.summary : []);
    } catch (err) {
      console.log(err);
      setRows([]);
      setSummary([]);
    } finally {
      setLoading(false);
    }
  }, [date, eventType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => [
      row.user_id,
      row.user_name,
      row.user_phone,
      row.amount,
      row.event_type,
      row.payment_method,
      row.ip_address,
      row.user_agent,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [query, rows]);

  const summaryMap = useMemo(() => {
    const map = new Map();
    summary.forEach((item) => map.set(String(item.event_type || ""), item));
    return map;
  }, [summary]);

  return (
    <AdminLayout>
      <section style={page}>
        <header style={header}>
          <div>
            <h1 style={title}>Payment Visits</h1>
            <p style={subtitle}>Track users who opened Add Money, selected amount, opened UPI, or submitted proof.</p>
          </div>
          <button type="button" style={refreshButton} onClick={loadData}>
            <RefreshCw size={16} /> Refresh
          </button>
        </header>

        <div style={summaryGrid}>
          <SummaryCard label="Page Visits" item={summaryMap.get("visit")} color="#2563eb" />
          <SummaryCard label="Amount Selected" item={summaryMap.get("amount_selected")} color="#f59e0b" />
          <SummaryCard label="UPI Opened" item={summaryMap.get("payment_app_opened")} color="#7c3aed" />
          <SummaryCard label="Proof Submitted" item={summaryMap.get("proof_submitted")} color="#16a34a" />
        </div>

        <section style={toolbar}>
          <label style={filterBox}>
            <CalendarDays size={16} />
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} style={input} />
          </label>
          <select value={eventType} onChange={(event) => setEventType(event.target.value)} style={select}>
            <option value="all">All Events</option>
            <option value="visit">Page Visits</option>
            <option value="amount_selected">Amount Selected</option>
            <option value="payment_app_opened">UPI Opened</option>
            <option value="proof_submitted">Proof Submitted</option>
          </select>
          <label style={searchBox}>
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search user, phone, amount..." style={input} />
          </label>
        </section>

        <section style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Time</th>
                <th style={th}>User</th>
                <th style={th}>Amount</th>
                <th style={th}>Event</th>
                <th style={th}>Method</th>
                <th style={th}>Deposit Requests</th>
                <th style={th}>Device / IP</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id} style={tr}>
                  <td style={td}>{formatDateTime(row.created_at)}</td>
                  <td style={td}>
                    <button type="button" style={userButton} onClick={() => navigate(`/admin/users/${row.user_id}`)}>
                      <strong>{row.user_name}</strong>
                      <span>{row.user_phone}</span>
                      <ExternalLink size={13} />
                    </button>
                  </td>
                  <td style={td}>{row.amount != null ? `Rs ${Number(row.amount || 0).toFixed(2)}` : "-"}</td>
                  <td style={td}><span style={eventBadge(row.event_type)}>{formatEvent(row.event_type)}</span></td>
                  <td style={td}>{row.payment_method || "-"}</td>
                  <td style={td}>{Number(row.deposit_requests_today || 0)}</td>
                  <td style={td}>
                    <div style={deviceBox}>
                      <span>{row.ip_address || "-"}</span>
                      <small>{trimDevice(row.user_agent)}</small>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredRows.length && (
                <tr>
                  <td style={emptyCell} colSpan={7}>{loading ? "Loading..." : "No payment visits found"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </section>
    </AdminLayout>
  );
}

function SummaryCard({ label, item, color }) {
  return (
    <article style={summaryCard(color)}>
      <CreditCard size={18} />
      <span>{label}</span>
      <strong>{Number(item?.count || 0)}</strong>
      <small>{Number(item?.users || 0)} users</small>
    </article>
  );
}

function formatEvent(value) {
  const key = String(value || "").toLowerCase();
  if (key === "visit") return "Visited Add Money";
  if (key === "amount_selected") return "Amount Selected";
  if (key === "payment_app_opened") return "UPI Opened";
  if (key === "proof_submitted") return "Proof Submitted";
  return key.replace(/_/g, " ");
}

function eventBadge(value) {
  const key = String(value || "").toLowerCase();
  const color = key === "proof_submitted" ? "#16a34a" : key === "payment_app_opened" ? "#7c3aed" : key === "amount_selected" ? "#d97706" : "#2563eb";
  return {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "7px 10px",
    background: `${color}14`,
    color,
    fontWeight: 900,
    fontSize: 12,
    textTransform: "capitalize",
  };
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function trimDevice(value) {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.length > 88 ? `${text.slice(0, 88)}...` : text;
}

const page = { display: "grid", gap: 16 };
const header = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" };
const title = { margin: 0, color: "#0f172a", fontSize: 26, fontWeight: 900 };
const subtitle = { margin: "5px 0 0", color: "#64748b", fontSize: 13 };
const refreshButton = { minHeight: 42, border: 0, borderRadius: 12, background: "#2563eb", color: "#fff", display: "inline-flex", alignItems: "center", gap: 8, padding: "0 14px", fontWeight: 900, cursor: "pointer" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 };
const summaryCard = (color) => ({ minHeight: 118, borderRadius: 18, padding: 16, background: `linear-gradient(135deg, ${color}, #0f172a)`, color: "#fff", display: "grid", alignContent: "space-between", boxShadow: `0 18px 36px ${color}24` });
const toolbar = { display: "grid", gridTemplateColumns: "190px 190px minmax(240px, 1fr)", gap: 10 };
const filterBox = { height: 42, display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "0 12px" };
const searchBox = { ...filterBox };
const input = { width: "100%", border: 0, outline: 0, background: "transparent", color: "#0f172a", fontSize: 14 };
const select = { height: 42, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", color: "#0f172a", padding: "0 12px", fontWeight: 800 };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, overflow: "auto" };
const table = { width: "100%", minWidth: 980, borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "13px 12px", color: "#475569", fontSize: 12, borderBottom: "1px solid #e2e8f0", textTransform: "uppercase" };
const tr = { borderBottom: "1px solid #f1f5f9" };
const td = { padding: "13px 12px", color: "#334155", fontSize: 13, verticalAlign: "middle" };
const userButton = { border: 0, background: "transparent", color: "#0f172a", display: "grid", gap: 3, padding: 0, textAlign: "left", cursor: "pointer" };
const deviceBox = { display: "grid", gap: 4, maxWidth: 300 };
const emptyCell = { padding: 34, textAlign: "center", color: "#94a3b8" };

export default AdminPaymentIntents;
